import React, { useEffect, useState } from 'react';
import './styles.css';
import '../../GraphRead/graph.css';
import { gql, useMutation } from '@apollo/client';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { Alert, SwipeableDrawer } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { CloudinaryUpload } from '../../CloudinaryUpload';
import { normalizeGraphType } from '../../graphTypes';
import dayjs from 'dayjs';

const EDIT_PERSON_MUTATION = gql`
  mutation EditPerson($editPersonId: ID!, $name: String!, $imageURL: String, $funFact: String, $birthDate: Date, $deathDate: Date) {
    editPerson(id: $editPersonId, name: $name, imageURL: $imageURL, funFact: $funFact, birthDate: $birthDate, deathDate: $deathDate) {
      _id
    }
  }
`;

const DELETE_PERSON_MUTATION = gql`
  mutation DeletePerson($id: ID!) {
    deletePerson(id: $id)
  }
`;

function getInitials(name = '') {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('') || '?';
}

function formatDate(date) {
  if (!date) return 'Unknown';
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function formatLifeDates(person) {
  if (!person?.birthDate && !person?.deathDate) return '';
  const born = person.birthDate ? formatDate(person.birthDate) : 'Unknown';
  const died = person.deathDate ? formatDate(person.deathDate) : 'Living';
  return `Born: ${born} - Died: ${died}`;
}

export const EditPersonModal = ({
  selectedNode,
  isModalOpen,
  setIsModalOpen,
  refetchGraph,
  headerBackgroundColor,
  graphType = 'social',
}) => {
  const [editPerson, { loading: isSaving, error: saveError }] = useMutation(EDIT_PERSON_MUTATION);
  const [deletePerson, { loading: isDeleting, error: deleteError }] = useMutation(DELETE_PERSON_MUTATION);
  const [editedNodeName, setEditedNodeName] = useState('');
  const [editedNodeImage, setEditedNodeImage] = useState('');
  const [editedFunFact, setEditedFunFact] = useState('');
  const [editedBirthDate, setEditedBirthDate] = useState(null);
  const [editedDeathDate, setEditedDeathDate] = useState(null);
  const isFamilyTree = normalizeGraphType(graphType) === 'family';
  const toMutationDate = (dateValue) => (dateValue?.toDate ? dateValue.toDate() : dateValue);

  const saveChanges = async () => {
    // Update the node with the edited name, image, and funFact
    await editPerson({
      variables: {
        editPersonId: selectedNode.id,
        name: editedNodeName,
        imageURL: editedNodeImage,
        funFact: editedFunFact,
        birthDate: toMutationDate(editedBirthDate),
        deathDate: toMutationDate(editedDeathDate),
      },
    });
    await refetchGraph();
    setIsModalOpen(false);
  };

  const deleteNode = async () => {
    const confirmed = window.confirm(`Delete ${selectedNode.name} and every story connected to them?`);
    if (!confirmed) return;

    await deletePerson({ variables: { id: selectedNode.id } });
    await refetchGraph();
    setIsModalOpen(false);
  };

  useEffect(() => {
    setEditedNodeName(selectedNode?.name);
    setEditedNodeImage(selectedNode?.image);
    setEditedFunFact(selectedNode?.funFact);
    setEditedBirthDate(selectedNode?.birthDate ? dayjs(selectedNode.birthDate) : null);
    setEditedDeathDate(selectedNode?.deathDate ? dayjs(selectedNode.deathDate) : null);
  }, [selectedNode]);

  if (!selectedNode || !isModalOpen) return null;

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={isModalOpen}
      onClose={() => setIsModalOpen(false)}
      onOpen={() => setIsModalOpen(true)}
      swipeAreaWidth={selectedNode ? 48 : 0}
      disableSwipeToOpen={false}
      ModalProps={{ keepMounted: true }}
      PaperProps={{
        sx: {
          height: 'min(86dvh, calc(100dvh - env(safe-area-inset-top, 0px)))',
          overflow: 'visible',
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          background: 'transparent',
          boxShadow: 'none',
        },
      }}
    >
      <section
        className="graph-detail-sheet graph-person-sheet graph-edit-detail-sheet"
        style={{
          '--detail-accent': headerBackgroundColor,
          '--detail-accent-soft': `color-mix(in srgb, ${headerBackgroundColor} 24%, transparent)`,
        }}
        aria-label={`Edit ${selectedNode.name}`}
      >
        <div className="graph-detail-puller" />
        <button type="button" className="graph-detail-close" onClick={() => setIsModalOpen(false)} aria-label="Close person editor">
          <CloseIcon fontSize="small" />
        </button>

        <header className="graph-person-hero">
          <div
            className="graph-detail-avatar graph-detail-avatar--hero"
            style={{ '--detail-accent': headerBackgroundColor }}
            aria-hidden="true"
          >
            {editedNodeImage ? (
              <img src={editedNodeImage} alt="" />
            ) : (
              <span>{getInitials(editedNodeName || selectedNode.name)}</span>
            )}
          </div>
          <div className="graph-person-hero-copy">
            <span>Edit person</span>
            <h2>{selectedNode.name}</h2>
            {formatLifeDates(selectedNode) && <small>{formatLifeDates(selectedNode)}</small>}
            <p>{selectedNode.funFact || 'Update their profile, photo, and details.'}</p>
          </div>
        </header>

        <form className="graph-edit-drawer-form" onSubmit={(event) => {
          event.preventDefault();
          saveChanges();
        }}>
          {(saveError || deleteError) && (
            <Alert severity="error">
              {saveError?.message || deleteError?.message}
            </Alert>
          )}
          <div className="graph-detail-section-header">
            <span>Profile</span>
            <strong>Edit core details</strong>
          </div>
          <div className="graph-edit-field-grid">
            <TextField
              type="text"
              value={editedNodeName}
              onChange={(e) => setEditedNodeName(e.target.value)}
              label="Name"
              fullWidth
            />
            <TextField
              type="text"
              value={editedNodeImage}
              onChange={(e) => setEditedNodeImage(e.target.value)}
              label="Image URL"
              fullWidth
            />
          </div>
          <TextField
            type="text"
            value={editedFunFact}
            onChange={(e) => setEditedFunFact(e.target.value)}
            label="Fun Fact"
            fullWidth
            multiline
            minRows={3}
          />
          {isFamilyTree && (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box className="graph-edit-field-grid">
                <DatePicker sx={{ width: '100%' }} label="Born" value={editedBirthDate} onChange={(value) => setEditedBirthDate(value)} />
                <DatePicker sx={{ width: '100%' }} label="Died" value={editedDeathDate} onChange={(value) => setEditedDeathDate(value)} />
              </Box>
            </LocalizationProvider>
          )}
          <CloudinaryUpload
            imageUrl={editedNodeImage}
            setImageUrl={setEditedNodeImage}
          />

          <div className="graph-edit-drawer-actions">
            <Button type="submit" variant="contained" disabled={isSaving || isDeleting || !editedNodeName}>
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
            <Button variant="outlined" onClick={() => setIsModalOpen(false)} disabled={isSaving || isDeleting}>
              Cancel
            </Button>
            <Button variant="contained" color="error" onClick={deleteNode} disabled={isSaving || isDeleting}>
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </form>
      </section>
    </SwipeableDrawer>
  );
};
